// InfluencerPageApi.js

import axios from 'axios';
import { ERROR_CODES } from '../constants/errors'; // Import error codes
import { handleApiError } from './handleApiError.js'; // Example import path

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export async function fetchProducts(token, influencerId) {
  const url = `${baseUrl}/user/api/getProducts/${influencerId}`;
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: (status) => status >= 200 && status < 500, // Handle non-2xx responses without throwing
    });

    if (response.status === 200) {
      // Success case: return the data directly
      return response.data; // Expect an array of product documents
    } else {
      // Backend returned an error status code (4xx, 5xx)
      return {
        error: response.data?.error || `HTTP_ERROR_${response.status}`, // Use backend error code if available
        message:
          response.data?.message ||
          `Request failed with status ${response.status}.`, // Use backend message
        data: response.data?.data, // Include extra data if provided
      };
    }
  } catch (error) {
    // Handle network errors or other issues during the request setup/execution
    console.error('[fetchProducts] Request error:', error);
    if (error.response) {
      // This case might be less likely with validateStatus, but good practice
      return {
        error:
          error.response.data?.error || `HTTP_ERROR_${error.response.status}`,
        message: error.response.data?.message || 'API request failed.',
        data: error.response.data?.data,
      };
    } else if (error.request) {
      // Request was made, but no response received
      return {
        error: ERROR_CODES.NETWORK_ERROR,
        message: 'Network error: No response received from server.',
      };
    } else {
      // Error occurred in setting up the request
      return {
        error: ERROR_CODES.REQUEST_ERROR,
        message:
          error.message ||
          'An unexpected error occurred during the request setup.',
      };
    }
  }
}

export const fetchSocialMedias = async (token, influencerId) => {
  const url = `${baseUrl}/user/api/listSocialMedias?influencerId=${influencerId}`;
  console.debug(
    '[fetchSocialMedias] Attempting to fetch social media records for influencerId:',
    influencerId,
  );
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      validateStatus: (status) => status >= 200 && status < 500, // Handle 4xx/5xx responses in the response block
    });

    if (response.status === 200) {
      console.debug(
        '[fetchSocialMedias] Successfully fetched social media records:',
        response.data,
      );
      return { status: true, data: response.data };
    } else {
      console.warn(
        `[fetchSocialMedias] Failed. Status: ${response.status}`,
        response.data,
      );
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Failed to fetch social media records (Status: ${response.status})`,
        statusCode: response.status,
      };
    }
  } catch (error) {
    return handleApiError(error, 'fetchSocialMedias');
  }
};

export const fetchSocialContacts = async (token, influencerId) => {
  const url = `${baseUrl}/user/api/listSocialContacts?influencerId=${influencerId}`;
  console.debug(
    '[fetchSocialContacts] Attempting to fetch social contact records for influencerId:',
    influencerId,
  );
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      validateStatus: (status) => status >= 200 && status < 500,
    });

    if (response.status === 200) {
      console.debug(
        '[fetchSocialContacts] Successfully fetched social contact records:',
        response.data,
      );
      return { status: true, data: response.data }; // Expect an array of social contact objects
    } else {
      console.warn(
        `[fetchSocialContacts] Failed. Status: ${response.status}`,
        response.data,
      );
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Failed to fetch social contact records (Status: ${response.status})`,
        statusCode: response.status,
      };
    }
  } catch (error) {
    // Use the centralized handleApiError function
    return handleApiError(error, 'fetchSocialContacts');
  }
};

export const addSocialContact = async (token, contactData) => {
  const url = `${baseUrl}/user/api/addSocialContact`;
  console.debug(
    '[addSocialContact] Attempting to add new social contact:',
    contactData,
  );
  try {
    const response = await axios.post(url, contactData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Successful creation (201)
    return { status: true, data: response.data };
  } catch (error) {
    // Centralized handler will parse the axios error object
    return handleApiError(error, 'addSocialContact');
  }
};

export const addSocialMedia = async (token, mediaData) => {
  const url = `${baseUrl}/user/api/addSocialMedia`;
  console.debug(
    '[addSocialMedia] Attempting to add new publishing channel:',
    mediaData,
  );
  try {
    const response = await axios.post(url, mediaData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Successful creation (201)
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'addSocialMedia');
  }
};

// --- NEW FUNCTIONS FOR EDITING ---
export const updateSocialContact = async (token, contactId, updates) => {
  const url = `${baseUrl}/user/api/updateSocialContact/${contactId}`;
  const payload = { updates };
  console.debug('[updateSocialContact] Attempting to update contact:', payload);
  try {
    const response = await axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'updateSocialContact');
  }
};

export const updateSocialMedia = async (token, mediaId, updates) => {
  const url = `${baseUrl}/user/api/updateSocialMedia/${mediaId}`;
  const payload = { updates };
  console.debug(
    '[updateSocialMedia] Attempting to update media channel:',
    payload,
  );
  try {
    const response = await axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'updateSocialMedia');
  }
};

export const updatePartnerInfo = async (token, influencerId, updates) => {
  const url = `${baseUrl}/user/api/updatePartnerInfo`;
  const payload = { influencerId, updates };
  console.debug(
    '[updatePartnerInfo] Attempting to update partner info:',
    payload,
  );
  try {
    const response = await axios.post(url, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'updatePartnerInfo');
  }
};

export const addNewInfluencer = async (token, influencerData) => {
  const url = `${baseUrl}/user/api/addNewInfluencer`;
  console.debug(
    '[addNewInfluencer] Attempting to add new influencer:',
    influencerData,
  );
  try {
    const response = await axios.post(url, influencerData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data }; // Expects { message, influencerId }
  } catch (error) {
    return handleApiError(error, 'addNewInfluencer');
  }
};

/**
 * Fetches the complete details for a single influencer.
 * This should match the data structure returned by the main query endpoints.
 * @param {string} token - The authorization token.
 * @param {number} influencerId - The ID of the influencer to fetch.
 * @returns {Promise<object>} - A promise that resolves to an object with status and data/error.
 */
export const getInfluencerDetails = async (token, influencerId) => {
  // Note: The backend route is named 'fetchQueryResults', which is a bit confusing.
  // We name the frontend function more clearly.
  const url = `${baseUrl}/user/api/fetchQueryResults/${influencerId}`;
  console.debug(
    '[getInfluencerDetails] Attempting to fetch details for influencerId:',
    influencerId,
  );
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // On success (200), the response.data will be the influencer object
    return { status: true, data: response.data };
  } catch (error) {
    // Let the centralized handler parse the error from the response
    return handleApiError(error, 'getInfluencerDetails');
  }
};

export const deleteInfluencer = async (token, influencerId) => {
  const url = `${baseUrl}/user/api/deleteInfluencer/${influencerId}`;
  console.debug(
    '[deleteInfluencer] Attempting to delete influencerId:',
    influencerId,
  );
  try {
    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'deleteInfluencer');
  }
};

/**
 * Triggers a lightweight health analysis refresh for a specific influencer.
 * @param {string} token - The authorization token.
 * @param {number} influencerId - The ID of the influencer to refresh.
 * @param {number} socialMediaId - The database ID of the specific social media channel.
 * @returns {Promise<object>} A promise that resolves to an object with status and data/error.
 */
export const refreshInfluencerHealth = async (
  token,
  influencerId,
  socialMediaId,
) => {
  const url = `${baseUrl}/user/api/influencers/${influencerId}/social-media/${socialMediaId}/refresh-health`;
  console.debug(
    `[refreshInfluencerHealth] Firing request for influencerId: ${influencerId} and socialMediaId: ${socialMediaId}`,
  );
  try {
    // Using POST as this action modifies data on the server.
    const response = await axios.post(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'refreshInfluencerHealth');
  }
};

/**
 * Fetches the detailed health dashboard data for an influencer.
 * @param {string} token - The authorization token.
 * @param {number} influencerId - The ID of the influencer.
 * @returns {Promise<object>} A promise that resolves to an object with status and data/error.
 */
export const fetchInfluencerHealthDashboard = async (token, influencerId) => {
  const url = `${baseUrl}/user/api/influencers/${influencerId}/health-dashboard`;
  console.debug(
    `[fetchInfluencerHealthDashboard] Firing request for influencerId: ${influencerId}`,
  );
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchInfluencerHealthDashboard');
  }
};

// --- NEW API WRAPPERS FOR CONTENT FOCUS ANALYSIS ---

/**
 * Triggers a content focus analysis refresh for a specific influencer.
 * @param {string} token - The authorization token.
 * @param {number} influencerId - The ID of the influencer to refresh.
 * @param {number} socialMediaId - The database ID of the specific social media channel.
 * @returns {Promise<object>} A promise that resolves to an object with status and the new dashboard data.
 */
export const refreshInfluencerContentFocus = async (
  token,
  influencerId,
  socialMediaId,
) => {
  const url = `${baseUrl}/user/api/influencers/${influencerId}/social-media/${socialMediaId}/refresh-content-focus`;
  console.debug(
    `[refreshInfluencerContentFocus] Firing request for influencerId: ${influencerId} and socialMediaId: ${socialMediaId}`,
  );
  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'refreshInfluencerContentFocus');
  }
};

/**
 * Fetches the detailed content focus dashboard data for an influencer's specific social media channel.
 * @param {string} token - The authorization token.
 * @param {number} influencerId - The ID of the influencer.
 * @param {number} socialMediaId - The database ID of the specific social media channel.
 * @returns {Promise<object>} A promise that resolves to an object with status and data/error.
 */
export const fetchInfluencerContentFocusDashboard = async (
  token,
  influencerId,
  socialMediaId,
) => {
  const url = `${baseUrl}/user/api/influencers/${influencerId}/social-media/${socialMediaId}/content-focus-dashboard`;
  console.debug(
    `[fetchInfluencerContentFocusDashboard] Firing request for influencerId: ${influencerId} and socialMediaId: ${socialMediaId}`,
  );
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return { status: true, data: response.data };
  } catch (error) {
    return handleApiError(error, 'fetchInfluencerContentFocusDashboard');
  }
};

// =========== NEW AGGREGATED API FUNCTION ===========
/**
 * Fetches all necessary data for the InfluencerPage in a single, aggregated request.
 * This includes influencer info, products, social medias, and social contacts.
 * @param {string} token - The authorization token.
 * @param {number} influencerId - The ID of the influencer.
 * @returns {Promise<object>} A promise that resolves to an object with { status, data, message?, error? }.
 */
export const fetchInfluencerPageData = async (token, influencerId) => {
  const url = `${baseUrl}/user/api/influencer-page-data/${influencerId}`;
  console.debug(
    '[fetchInfluencerPageData] Firing aggregated request for influencerId:',
    influencerId,
  );
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        // Note: The backend will set 'Cache-Control: no-cache', so we don't need to add it here.
      },
      // Use validateStatus to handle all HTTP responses within the .then() block
      validateStatus: (status) => status >= 200 && status < 500,
    });

    // --- Handle Successful Response ---
    if (response.status === 200) {
      console.debug(
        '[fetchInfluencerPageData] Successfully fetched aggregated data:',
        response.data,
      );
      return { status: true, data: response.data };
    }
    // --- Handle Backend Error Responses (4xx, 5xx) ---
    else {
      console.warn(
        `[fetchInfluencerPageData] Failed. Status: ${response.status}`,
        response.data,
      );
      return {
        status: false,
        error: response.data?.error || `HTTP_ERROR_${response.status}`,
        message:
          response.data?.message ||
          `Failed to fetch influencer page data (Status: ${response.status})`,
        statusCode: response.status,
      };
    }
  } catch (error) {
    // --- Handle Network Errors or other exceptions ---
    // Use the centralized handleApiError function for consistency
    return handleApiError(error, 'fetchInfluencerPageData');
  }
};
